import type { Ref } from 'vue'

import type {
  AnalyticsFilterDtoMetric,
  SegmentCriteriaDto,
  UserFilterDtoField,
  UserRefDto,
} from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'

import type { Money, ProcessableImage } from './common.types'

export enum SegmentCreatorSourceEnum {
  Manual = 'manual',
  Rule = 'rule',
}

export enum SegmentCreatorValueKindEnum {
  Platform = 'platform',
  RevenueTier = 'revenueTier',
  Client = 'client',
  User = 'user',
  Analytics = 'analytics',
}

export type SegmentCreatorReference = {
  id: number
  name: string | null
}

export type SegmentCreatorValue =
  | {
      kind: SegmentCreatorValueKindEnum.Platform
      platform: string
      handle: string | null
      followers: number | null
      /** The account is linked through the platform's API, not just claimed by handle. */
      isConnected: boolean
    }
  | ({ kind: SegmentCreatorValueKindEnum.RevenueTier } & SegmentCreatorReference)
  | ({ kind: SegmentCreatorValueKindEnum.Client } & SegmentCreatorReference)
  | {
      kind: SegmentCreatorValueKindEnum.User
      field: UserFilterDtoField
      date: string
    }
  | {
      kind: SegmentCreatorValueKindEnum.Analytics
      metric: AnalyticsFilterDtoMetric
      /** Money for every metric except conversion, which is a plain count. */
      value: Money | number
    }

export type SegmentCreator = {
  id: string
  username: string | null
  image: ProcessableImage | null
  source: SegmentCreatorSourceEnum
  matchedValues: SegmentCreatorValue[]
}

export type GetSegmentCreatorLink = (creator: SegmentCreator) => string | null

export type SegmentPreviewFilter = {
  advertiserGroupId?: number
  criteria: SegmentCriteriaDto | null
  manualMembers: UserRefDto[]
  excludedMembers: UserRefDto[]
  query: string
  page: number
  size: number
  /**
   * Set only for a saved static segment, whose stored membership the server reads back instead of
   * evaluating anything. Its criteria are frozen, so they are left out of the request entirely.
   */
  segmentId?: string
}

export type SegmentPreviewResult = {
  creators: SegmentCreator[]
  /** How many creators the search narrowed the segment down to. Pagination follows this one. */
  totalCount: number
  /** How big the whole segment is, whatever the search says. */
  totalMembers: number
  totalPages: number
  exceedsMemberCap: boolean
}

export type UseGetSegmentPreviewReturn = {
  data: Ref<SegmentCreator[]>
  totalCount: Ref<number>
  totalMembers: Ref<number>
  totalPages: Ref<number>
  exceedsMemberCap: Ref<boolean>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}
